/** @jsx jsx */

import { last, isEmpty, cloneDeep } from 'lodash';
import { Component, ReactNode } from 'react';
import { connectRouteTo } from 'redux-router-kit';

import { Breadcrumbs, Margin } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import { cloneComponentChild } from 'app/common/utils/ReactUtils';
import {
  findByKeyOrIndex,
  getActionUrl,
  getInputFields,
  isNewServiceField,
} from 'app/developer-v3/utils';
import {
  ACTION_INPUT_ROUTES as INPUT_ROUTES,
  ACTION_INPUT_TYPES as INPUT_TYPES,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import type { ErrorList } from 'app/developer-v3/types/validator';

import type {
  ActionInputParams,
  ActionInputType,
  ActionSchema,
  FormifiedFieldSchema,
} from 'app/developer-v3/types';
import type { FieldOrFunctionSchema } from 'app/developer-v3/platformSchema/field';

const NEW_SERVICE_LABEL = 'New';

const getLinkLabel = (label?: string, key?: string): string => {
  if (label) {
    return label;
  }

  if (key) {
    return key === NEW_SERVICE_FIELD ? NEW_SERVICE_LABEL : key;
  }

  return '';
};

type Props = {
  action: ActionSchema;
  attemptedSave: boolean;
  isReadOnly: boolean;
  isSaveDisabled: () => boolean;
  children: ReactNode;
  errors: ErrorList;
  formifiedField: FormifiedFieldSchema;
  isFormValid: boolean;
  isSaving: boolean;
  setInputField: (
    inputSelector: string | number,
    inputType: ActionInputType,
    childInputSelector?: string | number
  ) => void;
  saveActionChanges: () => void;
  updateInputFields: (inputFields: FieldOrFunctionSchema[]) => void;
  resetUnsavedEdits: () => void;
  params: ActionInputParams;
  routeTo: (url: string) => void;
  onFormChange: (form: FormifiedFieldSchema) => void;
};

type State = {
  action: ActionSchema;
  attemptedSave: boolean;
  formifiedField: FormifiedFieldSchema;
};

class ActionGroupInputEditor extends Component<Props, State> {
  state = {
    action: this.props.action,
    attemptedSave: false,
    formifiedField: this.props.formifiedField,
  };

  componentWillUnmount() {
    this.props.resetUnsavedEdits();
  }

  get inputField() {
    const { action, params } = this.props;
    const { inputKey } = params;
    const inputFields = getInputFields(action);
    return (
      (this.isInputFieldNew
        ? last(inputFields)
        : findByKeyOrIndex(inputFields, inputKey)) || {}
    );
  }

  get inputFieldUrl() {
    const {
      params: { inputKey },
    } = this.props;
    return `${this.inputFieldsUrl}/${inputKey}/${
      INPUT_ROUTES[INPUT_TYPES.GROUP]
    }/`;
  }

  get inputFieldsUrl() {
    return `${getActionUrl(this.props.params)}/input`;
  }

  get isChildInputFieldMounted() {
    return this.props.params.childInputKey !== undefined;
  }

  get isInputFieldNew() {
    return this.props.params.inputKey === NEW_SERVICE_FIELD;
  }

  get canSaveInputField() {
    if (this.isChildInputFieldMounted) {
      return !this.state.attemptedSave || this.props.isFormValid;
    }

    const isInputFieldCleanAndValid = !this.props.isSaveDisabled();
    if (!isInputFieldCleanAndValid) {
      return false;
    }

    const inputField = this.inputField;
    if (inputField && inputField.children) {
      const hasChildInputFields = !isEmpty(inputField.children);
      return hasChildInputFields;
    }

    if (inputField.source || inputField.require) {
      return true;
    }

    return false;
  }

  setInputField = () => {
    const { action, formifiedField, setInputField, params } = this.props;
    const { inputKey, childInputKey } = params;

    if (this.isChildInputFieldMounted) {
      setInputField(inputKey, INPUT_TYPES.INPUT, childInputKey);
      this.setState({
        action: cloneDeep(action),
        attemptedSave: false,
        formifiedField: cloneDeep(formifiedField),
      });
    } else {
      setInputField(inputKey, INPUT_TYPES.GROUP);
    }
  };

  resetInputField = () => {
    if (!this.state.attemptedSave) {
      if (this.isChildInputFieldMounted) {
        this.props.resetUnsavedEdits();
      } else {
        this.props.onFormChange(this.state.formifiedField);
        this.props.updateInputFields(getInputFields(this.state.action));
      }
    }
  };

  onSave = () => {
    const { saveActionChanges, routeTo } = this.props;

    if (this.isChildInputFieldMounted) {
      this.setState({ attemptedSave: true }, () => {
        if (isEmpty(this.props.errors) && this.canSaveInputField) {
          routeTo(this.inputFieldUrl);
        }
      });
    } else {
      saveActionChanges();
    }
  };

  onCancel = () => {
    this.props.routeTo(
      this.isChildInputFieldMounted ? this.inputFieldUrl : this.inputFieldsUrl
    );
  };

  renderBreadcrumb = () => {
    const {
      formifiedField: { label, key } = {},
      params: { inputKey, childInputKey } = {},
    } = this.props;

    const crumbs: ReactNode[] = [
      <Link aria-current="step" href={this.inputFieldsUrl}>
        Inputs
      </Link>,
    ];

    if (childInputKey) {
      const inputFields = getInputFields(this.props.action);
      const inputField =
        (isNewServiceField(inputKey)
          ? last(inputFields)
          : findByKeyOrIndex(inputFields, inputKey)) || {};

      crumbs.push(
        <Link aria-current="step" href={this.inputFieldUrl}>
          {getLinkLabel(inputField.label || '', inputField.key || '')}
        </Link>,
        <Link aria-current="step" href={`#${getLinkLabel(label, key)}`}>
          {getLinkLabel(label, key)}
        </Link>
      );
    } else {
      if (key) {
        crumbs.push(
          <Link aria-current="step" href={`#${getLinkLabel(label, key)}`}>
            {getLinkLabel(label, key)}
          </Link>
        );
      }
    }

    return (
      <Margin margin="0px 0px 30px 0px">
        <Breadcrumbs>{crumbs}</Breadcrumbs>
      </Margin>
    );
  };

  render() {
    const { children, isReadOnly, ...restProps } = this.props;
    const { attemptedSave } = this.isChildInputFieldMounted
      ? this.state
      : this.props;

    return (
      <EditorBodyLayout
        isDisabled={!this.canSaveInputField || isReadOnly}
        isSaving={restProps.isSaving}
        onCancel={this.onCancel}
        onSave={this.onSave}
      >
        {this.renderBreadcrumb()}
        {cloneComponentChild(children, {
          ...restProps,
          attemptedSave,
          onCancel: this.onCancel,
          setInputField: this.setInputField,
          resetInputField: this.resetInputField,
          isReadOnly,
        })}
      </EditorBodyLayout>
    );
  }
}

export default connectRouteTo(ActionGroupInputEditor);
